
import React, { useState } from 'react';
import { TreatmentSession } from '@/types/treatmentSession';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { format, parseISO } from 'date-fns';
import { CalendarDays, Clock } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SessionSchedulerProps {
  session: TreatmentSession;
  onReschedule: (date: string) => void;
  onCancel: () => void;
}

const SessionScheduler: React.FC<SessionSchedulerProps> = ({
  session,
  onReschedule,
  onCancel
}) => {
  const [date, setDate] = useState<Date>(parseISO(session.date));
  const [hour, setHour] = useState<string>(format(parseISO(session.date), 'HH'));
  const [minute, setMinute] = useState<string>(format(parseISO(session.date), 'mm'));
  
  const handleReschedule = () => {
    // Create new date with selected time
    const newDate = new Date(date);
    newDate.setHours(parseInt(hour));
    newDate.setMinutes(parseInt(minute));
    
    onReschedule(newDate.toISOString());
  };
  
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CalendarDays className="h-4 w-4 mr-2 text-primary" />
            Select New Date
          </h3>
          <Calendar
            mode="single"
            selected={date}
            onSelect={(newDate) => newDate && setDate(newDate)}
            className="rounded-md border"
          />
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <Clock className="h-4 w-4 mr-2 text-primary" />
            Select New Time
          </h3>
          <div className="flex gap-2">
            <div className="w-1/2">
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger>
                  <SelectValue placeholder="Hour" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString().padStart(2, '0')}>
                      {i.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-1/2">
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger>
                  <SelectValue placeholder="Minute" />
                </SelectTrigger>
                <SelectContent>
                  {['00', '15', '30', '45'].map((min) => (
                    <SelectItem key={min} value={min}>
                      {min}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="pt-4 border-t">
        <p className="text-sm mb-2">
          Current Date & Time: <span className="font-medium">{format(parseISO(session.date), 'EEEE, MMMM d, yyyy')} at {format(parseISO(session.date), 'h:mm a')}</span>
        </p>
        <p className="text-sm mb-4">
          New Date & Time: <span className="font-medium">{format(date, 'EEEE, MMMM d, yyyy')} at {parseInt(hour) > 12 ? parseInt(hour) - 12 : hour}:{minute} {parseInt(hour) >= 12 ? 'PM' : 'AM'}</span>
        </p>
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleReschedule}>
          Reschedule Session
        </Button>
      </div>
    </div>
  );
};

export default SessionScheduler;
